import { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Row,
  Col,
  Alert,
  Spinner,
  Form,
  ToggleButtonGroup,
  ToggleButton,
} from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { sessionType, genderType, teamScoreType } from '../../redux/_constants';
import {
  sessionActions,
  alertActions,
  adminActions,
} from '../../redux/_actions';
import * as Yup from 'yup';
import * as Defaults from '../../utilities/constants';
import {
  strApparatusToArray,
  arrayToStrApparatus,
  /*dateTime,*/ displayName,
} from '../../utilities/conversions';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import TeamSelector from '../setup/teamselector';
import { dataUtilities } from '../../utilities/data';
import { prettyTeamScoring } from '../../utilities/scoring';
import { useHistory } from 'react-router-dom';
import { JudgePanelType, SessionHostType } from '../../models';
import { API, graphqlOperation } from 'aws-amplify';
import { userByEmail } from '../../graphql/graphql';

function SessionForm(props) {
  const {
    setup,
    gender,
    reset,
    initialRtnId,
    initialStartAt,
    initialSessionKey,
    initialTeams = {},
    initialLocation,
    initialCompetition,
    initialAlternating,
    initialTeamScoring,
    initialJudgePanel,
    initialApparatus,
    initialHostId,
    initialHostType,
    initialScorekeepers,
    initialTitleSuffix,
    initialTitle,
  } = props;
  const [isCreating, setIsCreating] = useState(false);
  const [footerDisabled, setFooterDisabled] = useState(false);
  const [teamSelector, setTeamSelector] = useState(null);
  const [scorekeeperValidation, setScorekeeperValidation] = useState({});

  // Calculate initial visible team slots for multi sessions
  const getInitialTeamSlots = () => {
    if (setup !== sessionType.MULTI) return 2;
    // Count pre-selected teams from URL params
    const preSelectedCount = [
      'teamA',
      'teamB',
      'teamC',
      'teamD',
      'teamE',
      'teamF',
      'teamG',
      'teamH',
    ].filter((key) => initialTeams[key]).length;
    // Use max of pre-selected count or default of 4
    return Math.max(preSelectedCount, 4);
  };
  const [visibleTeamSlots, setVisibleTeamSlots] = useState(getInitialTeamSlots);
  const dispatch = useDispatch();
  const history = useHistory();
  const { alert } = useSelector((state) => state);
  const adminTeams = useSelector((state) => state.admin.teams);
  const [teams, setTeams] = useState(adminTeams);
  const { MALE, FEMALE, COED } = genderType;
  const { SOLO, DUAL, MULTI } = sessionType;
  const {
    MEDIAURL,
    mApparatusAbbv,
    wApparatusAbbv,
    soloDefault,
    dualDefault,
    dualNCAAMDefault,
    dualNCAAWDefault,
    multiDefault,
    multiNCAAMDefault,
    multiNCAAWDefault,
  } = Defaults;

  const abbv =
    gender === MALE ? mApparatusAbbv : gender === FEMALE ? wApparatusAbbv : [];

  const isMALE = gender === MALE;
  const isFEMALE = gender === FEMALE;

  useEffect(() => {
    if (!alert.clear) {
      setIsCreating(false);
    }
  }, [alert.clear]);

  // TODO: refactor to move admin function to data function
  useEffect(() => {
    if (teams.allIds.length === 0) {
      dispatch(adminActions.getTeams());
    }
  }, [dispatch, teams]);

  // Sync local teams state with Redux when adminTeams updates
  useEffect(() => {
    if (adminTeams.allIds.length > 0) {
      setTeams(adminTeams);
    }
  }, [adminTeams]);

  // Validate scorekeeper emails on load (in parallel with team loading)
  useEffect(() => {
    if (!initialScorekeepers) return;

    const emails = initialScorekeepers
      .split(',')
      .map((e) => e.trim().toLowerCase());

    // Initialize all as 'loading'
    const initialState = {};
    emails.forEach((email) => {
      initialState[email] = { status: 'loading', user: null };
    });
    setScorekeeperValidation(initialState);

    // Validate each email in parallel
    emails.forEach(async (email) => {
      try {
        const res = await API.graphql(graphqlOperation(userByEmail, { email }));
        const users = res.data.UserByEmail.items;

        setScorekeeperValidation((prev) => ({
          ...prev,
          [email]: {
            status: users.length > 0 ? 'valid' : 'not_found',
            user: users.length > 0 ? users[0] : null,
          },
        }));
      } catch (error) {
        console.error(`Error validating scorekeeper ${email}:`, error);
        setScorekeeperValidation((prev) => ({
          ...prev,
          [email]: { status: 'error', user: null },
        }));
      }
    });
  }, [initialScorekeepers]);

  const validationSchema = Yup.object().shape({
    judging: Yup.boolean(),
    judgingRequired: Yup.boolean(),
    judgePanel: Yup.string(),
    access: Yup.string(),
    now: Yup.boolean(),
    name: Yup.string(),
    judgeCount: Yup.number(),
    teamA: Yup.string().nullable(),
    teamB: Yup.string().nullable(),
    lineup: Yup.boolean(),
    competition: Yup.boolean(),
    apparatus: Yup.string(),
    startAt: Yup.string(),
    teamScoring: Yup.string().required('Required'),
    alternating: Yup.boolean(),
    //hostId: Yup.string(),
    //hostType: Yup.string(),
  });

  const getDefault = () => {
    let result = null;

    if (setup === SOLO) {
      result = {
        ...soloDefault,
        gender: gender,
        apparatus: strApparatusToArray(
          soloDefault.apparatus.substring(0, abbv.length)
        ),
        hostId: '',
        hostType: SessionHostType.TEAM,
      };
    }

    if (setup === DUAL) {
      result = {
        ...dualDefault,
        ...(isMALE ? dualNCAAMDefault : {}),
        ...(isFEMALE ? dualNCAAWDefault : {}),
        apparatus: strApparatusToArray(
          dualDefault.apparatus.substring(0, abbv.length)
        ),
      };
    }

    if (setup === MULTI) {
      result = {
        ...multiDefault,
        ...(isMALE ? multiNCAAMDefault : {}),
        ...(isFEMALE ? multiNCAAWDefault : {}),
        apparatus: strApparatusToArray(
          multiDefault.apparatus.substring(0, abbv.length)
        ),
      };
    }

    // Apply URL parameter overrides
    if (result) {
      if (initialRtnId) {
        result.rtnId = initialRtnId;
      }
      if (initialStartAt) {
        const parsedDate = new Date(initialStartAt);
        if (!isNaN(parsedDate.getTime())) {
          result.startAt = parsedDate;
        }
      }
      if (initialSessionKey) {
        result.sessionKey = initialSessionKey;
      }
      // Apply team overrides from URL
      if (initialTeams.teamA) result.teamA = initialTeams.teamA;
      if (initialTeams.teamB) result.teamB = initialTeams.teamB;
      if (initialTeams.teamC) result.teamC = initialTeams.teamC;
      if (initialTeams.teamD) result.teamD = initialTeams.teamD;
      if (initialTeams.teamE) result.teamE = initialTeams.teamE;
      if (initialTeams.teamF) result.teamF = initialTeams.teamF;
      if (initialTeams.teamG) result.teamG = initialTeams.teamG;
      if (initialTeams.teamH) result.teamH = initialTeams.teamH;
      if (initialLocation) result.location = initialLocation;
      // Apply additional form overrides from URL
      if (initialCompetition !== undefined && initialCompetition !== '') {
        result.competition =
          initialCompetition === '1' || initialCompetition === 'true';
      }
      if (initialAlternating !== undefined && initialAlternating !== '') {
        result.alternating =
          initialAlternating === '1' || initialAlternating === 'true';
      }
      if (initialTeamScoring) result.teamScoring = initialTeamScoring;
      if (initialJudgePanel) result.judgePanel = initialJudgePanel;
      if (initialApparatus) {
        result.apparatus = strApparatusToArray(initialApparatus);
      }
      if (initialHostId) result.hostId = initialHostId;
      if (initialHostType) result.hostType = initialHostType;
      if (initialTitle) result.name = initialTitle;
    }

    return result;
  };

  const { setFieldValue, handleSubmit, handleChange, values } = useFormik({
    initialValues: getDefault(),
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit(values) {
      setIsCreating(true);
      const input = {
        ...values,
        type: setup,
        name: calcSessionName(),
        apparatus: arrayToStrApparatus(values.apparatus),
        judgePanel: getJudgePanel(gender),
        scorekeepers: initialScorekeepers,
      };

      const teamChange = dataUtilities.checkSessionTeamChange(input);

      // For hostId removed, we need to remove the values.
      if (!input?.hostId) {
        delete input?.hostType;
        delete input?.hostId;
      }

      dispatch(alertActions.clear());
      dispatch(sessionActions.create(input, teamChange, history));
    },
  });

  const getJudgePanel = (gender) => {
    const { NCAAW2_SVU, NCAAM, OPEN } = JudgePanelType;

    switch (gender) {
      case MALE:
        return NCAAM;
      case FEMALE:
        return NCAAW2_SVU;
      case COED:
      default:
        return OPEN;
    }
  };

  const calcSessionName = () => {
    const getName = (teamId) => {
      if (!teams?.byId?.[teamId]) return 'Team ?';
      return displayName(teams.byId[teamId].name, teams.byId[teamId].altNames);
    };

    const {
      teamA,
      teamB,
      teamC,
      teamD,
      teamE,
      teamF,
      teamG,
      teamH,
      competition,
      name,
    } = values;
    let result = teamA === '' ? 'Team ?' : getName(teamA);

    if (name === '') {
      if (setup === DUAL) {
        result += ' vs ';
        result += teamB === '' ? 'Team ?' : getName(teamB);
      }

      if (setup === MULTI) {
        result += teamB ? ` / ${getName(teamB)}` : '';
        result += teamC ? ` / ${getName(teamC)}` : '';
        result += teamD ? ` / ${getName(teamD)}` : '';
        result += teamE ? ` / ${getName(teamE)}` : '';
        result += teamF ? ` / ${getName(teamF)}` : '';
        result += teamG ? ` / ${getName(teamG)}` : '';
        result += teamH ? ` / ${getName(teamH)}` : '';
      } else {
        result += `${competition ? '' : ' in Practice'}`;
      }

      // Append title suffix if provided (e.g., "(DEMO)")
      if (initialTitleSuffix) {
        result += ` ${initialTitleSuffix}`;
      }

      return result;
    }

    return name;
  };

  const setTeam = (side, team = null) => {
    if (side !== null) {
      setFieldValue(`team${side}`, team ? team.id : '');
      // If this is team A and hostType is 'TEAM', set the hostId to the team's id
      if (side === 'A' && values.hostType === SessionHostType.TEAM && team) {
        setFieldValue('hostId', team.id);
      }
    }
    setTeamSelector(null);
    setFooterDisabled(false);
  };

  const getLogo = (side) => {
    const selectedTeamId = values[`team${side}`];
    let logo = '';

    // Check if we have a team ID but teams haven't loaded yet
    const isLoadingTeam = selectedTeamId && teams.allIds.length === 0;

    if (selectedTeamId && teams?.byId?.[selectedTeamId]) {
      logo =
        teams.byId[selectedTeamId].logos &&
        JSON.parse(teams.byId[selectedTeamId].logos).metaData.filename;
    }

    if (logo) {
      return {
        content: (
          <img
            className="img-thumbnail"
            src={`${MEDIAURL}${logo}`}
            alt={`${teams.byId[selectedTeamId]?.name || 'Team'} Logo`}
          />
        ),
        isQuestionMark: false,
      };
    }

    if (isLoadingTeam) {
      return {
        content: <span className="loading-question-mark">?</span>,
        isQuestionMark: true,
      };
    }

    return {
      content: '?',
      isQuestionMark: true,
    };
  };

  const goTeamSelector = (side) => {
    setTeamSelector(side);
    setFooterDisabled(true);
  };

  const teamWidget = (side, logo) => {
    return (
      <Col>
        <label>Team {side}</label>
        <Button
          variant={logo.isQuestionMark ? 'light' : 'outline-light'}
          disabled={(setup === SOLO && side === 'B') || isCreating}
          className="teamSelect"
          onClick={() => goTeamSelector(side)}
          style={{ fontSize: logo.isQuestionMark ? '5rem' : null }}
        >
          {logo.content}
        </Button>
      </Col>
    );
  };

  const setupBody = () => {
    const logos = {
      A: getLogo('A'),
      B: getLogo('B'),
      C: getLogo('C'),
      D: getLogo('D'),
      E: getLogo('E'),
      F: getLogo('F'),
      G: getLogo('G'),
      H: getLogo('H'),
    };

    return (
      <>
        {!alert.clear ? (
          <Row>
            <Alert
              dismissible
              onClose={() => dispatch(alertActions.clear())}
              variant={alert.type === 'alert-danger' ? 'danger' : 'success'}
            >
              {alert.message}
            </Alert>
          </Row>
        ) : null}
        <Row>
          <div
            className={[
              'setupFormLine',
              'teamSelection',
              setup === MULTI ? `multi ${gender.toLowerCase()}` : null,
            ].join(' ')}
          >
            {teamWidget('A', logos['A'])}
            {setup !== MULTI ? (
              <Col className="vsCol">
                <label>&nbsp;</label>
                <div
                  className={['vs', setup === SOLO ? 'disabled' : null].join(
                    ' '
                  )}
                >
                  <span className="vBlue">V</span>
                  <span className="vGray">S</span>
                </div>
              </Col>
            ) : null}
            {teamWidget('B', logos['B'])}
            {setup === MULTI && visibleTeamSlots >= 3
              ? teamWidget('C', logos['C'])
              : null}
            {setup === MULTI && visibleTeamSlots >= 4
              ? teamWidget('D', logos['D'])
              : null}
            {setup === MULTI && visibleTeamSlots >= 5
              ? teamWidget('E', logos['E'])
              : null}
            {setup === MULTI && visibleTeamSlots >= 6
              ? teamWidget('F', logos['F'])
              : null}
            {setup === MULTI && visibleTeamSlots >= 7
              ? teamWidget('G', logos['G'])
              : null}
            {setup === MULTI && visibleTeamSlots >= 8
              ? teamWidget('H', logos['H'])
              : null}
            {setup === MULTI && visibleTeamSlots < 8 ? (
              <Col
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Button
                  variant="link"
                  onClick={() => setVisibleTeamSlots(visibleTeamSlots + 1)}
                  disabled={isCreating}
                  style={{
                    fontSize: '0.75rem',
                    color: '#6c757d',
                    textDecoration: 'none',
                    whiteSpace: 'nowrap',
                    padding: '2px 4px',
                  }}
                >
                  + Add
                </Button>
              </Col>
            ) : null}
          </div>
        </Row>
        <Row>
          <div className="setupFormLine">
            <Col>
              <label>When</label>
              <Form.Check
                type="switch"
                label="Now"
                id="now"
                checked={values.now}
                onChange={handleChange}
                disabled={isCreating}
              />
            </Col>
            <Col>
              <label>Date / Time</label>
              <DatePicker
                name="startAt"
                disabled={values.now || isCreating}
                selected={values.startAt}
                onChange={(value) => setFieldValue('startAt', value)}
                showTimeSelect
                minDate={new Date()}
                dateFormat="MM/dd/yyyy h:mm aa"
                popperPlacement="auto"
              />
            </Col>
          </div>
        </Row>
        <Row>
          <div className="setupFormLine">
            <Col>
              <label>Format</label>
              <Form.Check
                type="switch"
                label="Competition"
                id="competition"
                checked={values.competition}
                onChange={handleChange}
                disabled={isCreating}
              />
            </Col>
            <Col>
              <label>Scoring</label>
              <Form.Control
                as="select"
                label="Required"
                id="teamScoring"
                value={prettyTeamScoring(values.teamScoring)}
                onChange={(e) =>
                  setFieldValue(
                    'teamScoring',
                    teamScoreType[
                      Object.keys(teamScoreType)[e.target.selectedIndex - 1]
                    ]
                  )
                }
                disabled={isCreating}
              >
                <option value="Select..." disabled hidden>
                  Select...
                </option>
                {Object.values(teamScoreType).map((type, i) => {
                  return <option key={i}>{prettyTeamScoring(type)}</option>;
                })}
              </Form.Control>
            </Col>
          </div>
        </Row>
        <Row>
          <div className="setupFormLine">
            <Col>
              <label>{`Apparatus (${values.apparatus.length}/${abbv.length})`}</label>
              <ToggleButtonGroup
                name="apparatus"
                className="apparatusToggle"
                type="checkbox"
                value={values.apparatus}
                onChange={(value) => {
                  if (value.length > 0) {
                    setFieldValue('apparatus', value);
                  }
                }}
              >
                {abbv.map((a, i) => {
                  return (
                    <ToggleButton
                      variant="outline-primary"
                      className="vCenter"
                      value={i}
                      key={i}
                      disabled={isCreating}
                    >
                      {a}
                    </ToggleButton>
                  );
                })}
              </ToggleButtonGroup>
            </Col>
          </div>
        </Row>
        <Row>
          <div className="setupFormLine">
            <Col>
              <label>Judging</label>
              <Form.Check
                type="switch"
                label="Enabled"
                id="judging"
                onChange={handleChange}
                checked={values.judging}
                disabled={isCreating}
              />
            </Col>
            <Col>
              <label>Alternating</label>
              <Form.Check
                type="switch"
                label="Enabled"
                id="alternating"
                onChange={handleChange}
                checked={values.alternating}
                disabled={isCreating}
              />
            </Col>
            <Col>
              <label>Host</label>
              <Form.Check
                type="switch"
                label="Team A"
                id="host"
                checked={values.hostType === SessionHostType.TEAM}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFieldValue('hostType', SessionHostType.TEAM);
                    if (values.teamA) {
                      setFieldValue('hostId', values.teamA);
                    }
                  } else {
                    setFieldValue('hostType', '');
                    setFieldValue('hostId', '');
                  }
                }}
                disabled={isCreating}
              />
            </Col>
          </div>
        </Row>
        <Row>
          <div className="setupFormLine">
            <Col>
              <label>Session Name</label>
              <input
                name="name"
                type="text"
                autoComplete="off"
                onChange={handleChange}
                value={calcSessionName()}
                disabled={isCreating}
              />
              <div className="codeSpecs">
                Autogenerated or custom name.
                {calcSessionName().length >= 50 && (
                  <span style={{ color: '#dc3545' }}>
                    {' '}
                    (Length: {calcSessionName().length}, max &lt; 50)
                  </span>
                )}
              </div>
            </Col>
          </div>
        </Row>
        {(initialRtnId ||
          initialSessionKey ||
          initialLocation ||
          initialScorekeepers) && (
          <Row>
            <div className="setupFormLine">
              <Col>
                <div
                  className="codeSpecs"
                  style={{
                    marginTop: '10px',
                    padding: '8px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '4px',
                  }}
                >
                  {initialRtnId && (
                    <div>
                      <strong>Meet ID:</strong> {initialRtnId}
                    </div>
                  )}
                  {initialSessionKey && (
                    <div>
                      <strong>Session Key:</strong> {initialSessionKey}
                    </div>
                  )}
                  {initialLocation && (
                    <div>
                      <strong>Location:</strong> {initialLocation}
                    </div>
                  )}
                  {initialScorekeepers && (
                    <div>
                      <strong>Scorekeepers:</strong>
                      <div style={{ marginTop: '4px' }}>
                        {initialScorekeepers
                          .split(',')
                          .map((e) => e.trim().toLowerCase())
                          .map((email) => {
                            const validation = scorekeeperValidation[email];
                            const status = validation?.status || 'loading';
                            const userName = validation?.user?.name;

                            return (
                              <div
                                key={email}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  marginBottom: '2px',
                                }}
                              >
                                {status === 'loading' && (
                                  <Spinner
                                    animation="border"
                                    size="sm"
                                    style={{ width: '12px', height: '12px' }}
                                  />
                                )}
                                {status === 'valid' && (
                                  <span style={{ color: 'green' }}>✓</span>
                                )}
                                {status === 'not_found' && (
                                  <span style={{ color: 'red' }}>✗</span>
                                )}
                                {status === 'error' && (
                                  <span style={{ color: 'orange' }}>⚠</span>
                                )}
                                <span>
                                  {userName ? `${userName} (${email})` : email}
                                </span>
                                {status === 'not_found' && (
                                  <span
                                    style={{
                                      color: 'red',
                                      fontSize: '0.85em',
                                    }}
                                  >
                                    — user not found
                                  </span>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </div>
              </Col>
            </div>
          </Row>
        )}
      </>
    );
  };

  const setupFooter = () => {
    return (
      <>
        <Row>
          <Button
            variant="outline-primary"
            className="createButton"
            onClick={handleSubmit}
            disabled={isCreating || footerDisabled}
          >
            {isCreating ? (
              <>
                {'Creating session... '}
                <Spinner
                  variant="primary"
                  role="status"
                  animation="border"
                  size="sm"
                />
              </>
            ) : (
              'Create session'
            )}
          </Button>
        </Row>
        <Row>
          <div className="modalLink">
            <span>Back to</span>
            <button
              type="button"
              className="linkButton"
              disabled={isCreating || footerDisabled}
              onClick={() => reset()}
            >
              select session
            </button>
          </div>
        </Row>
      </>
    );
  };

  const headerMsg = () => {
    const solo = (
      <>
        <span className="vBlue">S</span>
        <span className="vGray">olo</span>
      </>
    );

    const dual = (
      <>
        <span className="vBlue">D</span>
        <span className="vGray">ual</span>
      </>
    );

    const multi = (
      <>
        <span className="vBlue">M</span>
        <span className="vGray">ulti</span>
      </>
    );

    const mens = (
      <>
        <span className="vBlue">M</span>
        <span className="vGray">ens</span>
      </>
    );

    const womens = (
      <>
        <span className="vBlue">W</span>
        <span className="vGray">omens</span>
      </>
    );

    const sessionLabel = () => {
      if (setup === SOLO) {
        return solo;
      }
      if (setup === DUAL) {
        return dual;
      }
      if (setup === MULTI) {
        return multi;
      }
    };

    const genderLabel = () => {
      if (gender === MALE) {
        return mens;
      }

      if (gender === FEMALE) {
        return womens;
      }
    };

    return (
      <>
        {genderLabel()}
        <span>&nbsp;</span>
        {sessionLabel()}
        <span>&nbsp;session setup</span>
      </>
    );
  };

  return (
    <>
      <Modal.Header className="createOrJoinHeader">{headerMsg()}</Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body className="createOrJoinForm">
          {setupBody()}
          {teamSelector ? (
            <TeamSelector
              side={teamSelector}
              setTeam={setTeam}
              values={values}
              isMulti={setup === MULTI}
              setTeams={setTeams}
              teams={teams}
            />
          ) : null}
        </Modal.Body>
        <Modal.Footer className="createOrJoinForm">
          {setupFooter()}
        </Modal.Footer>
      </Form>
    </>
  );
}

export default SessionForm;
